import { Typography } from '@/components/ui/typography';
import { formatPriceDisplay } from '../../../../util/formatting';
import PromoCodeInput from './PromoCodeInput';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppliedPromoDetails {
  code: string;
  discountType: string;
  discountValue: number;
}

interface PaymentOrderSummaryProps {
  subTotalAmount: number;
  totalAmount: number;
  savingsAmount: number;
  isPromoApplied: boolean;
  appliedPromoDetails: AppliedPromoDetails | null;
  // Promo code props
  promoCode: string | null;
  onPromoCodeChange: (value: string) => void;
  onApplyPromo: () => void;
  onRemovePromo: () => void;
  isPromocodeInvalid: boolean;
  promocodeMessage: string;
  promoCodeLoading: boolean;
  promoInputResetKey: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Deliberately NOT routed through formatPriceDisplay (DV-2935). This labels
// the promo's *configured* discount value, not a computed money amount, so it
// stays whole-dollar to match the promo admin's `fmtDiscount` ("$15 off", not
// "$15.00 off"). The savings amount below it is an amount, and does get cents.
function formatDiscountLabel(details: AppliedPromoDetails): string {
  return details.discountType === '%_discount'
    ? `${details.discountValue}% off`
    : `$${details.discountValue} off`;
}

export default function PaymentOrderSummary({
  subTotalAmount,
  totalAmount,
  savingsAmount,
  isPromoApplied,
  appliedPromoDetails,
  promoCode,
  onPromoCodeChange,
  onApplyPromo,
  onRemovePromo,
  isPromocodeInvalid,
  promocodeMessage,
  promoCodeLoading,
  promoInputResetKey,
}: PaymentOrderSummaryProps) {
  return (
    <div className="rounded-lg border border-border bg-background p-5">
      <Typography as="h2" variant="heading-md" className="mb-4">
        Order Summary
      </Typography>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Typography variant="body">Subtotal</Typography>
          <Typography variant="body-medium">
            ${formatPriceDisplay(subTotalAmount)}
          </Typography>
        </div>

        {isPromoApplied && (
          <div className="flex items-start justify-between">
            <div>
              {/* Both lines need `as` — Typography renders <span> by default,
                  and two spans here collapsed onto one line with no separator
                  ("DEMO11 applied$37.09 off"). */}
              <Typography as="p" variant="body-medium">
                {appliedPromoDetails?.code
                  ? `${appliedPromoDetails.code} applied`
                  : 'Savings'}
              </Typography>
              {appliedPromoDetails && (
                <Typography
                  as="p"
                  variant="body"
                  className="text-muted-foreground"
                >
                  {formatDiscountLabel(appliedPromoDetails)}
                </Typography>
              )}
            </div>
            {/* Minus sign, not accounting parentheses — matches the Order
                Service dialog's savings line so one convention reads the same
                everywhere (DV-2935). */}
            <Typography variant="body-medium" className="text-success">
              -${formatPriceDisplay(savingsAmount)}
            </Typography>
          </div>
        )}
      </div>

      <PromoCodeInput
        promoCode={promoCode}
        onPromoCodeChange={onPromoCodeChange}
        onApply={onApplyPromo}
        onRemove={onRemovePromo}
        isPromoApplied={isPromoApplied}
        isPromocodeInvalid={isPromocodeInvalid}
        promocodeMessage={promocodeMessage}
        isLoading={promoCodeLoading}
        inputResetKey={promoInputResetKey}
      />

      <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
        <Typography variant="body-semibold">Total</Typography>
        <Typography variant="heading-md">
          ${formatPriceDisplay(totalAmount)}
        </Typography>
      </div>
    </div>
  );
}
